import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Lathe } from "@react-three/drei";
import { forwardRef, useMemo } from "react";
import { TransformableItemProperties } from '../../core/Item';
import { Euler } from "three";
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface LatheMeshProperties extends TransformableItemProperties {
  segments: number;
  phiStart: number;
  phiLength: number;
  materialId?: string;
}

export interface LatheMesh extends Mesh<LatheMeshProperties> {
  subtype: 'lathe';
  properties: LatheMeshProperties;
}

// Create a reusable Lathe component that can be used in both preview and world
const LatheComponent = forwardRef<THREE.Mesh, { properties: LatheMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { segments, phiStart, phiLength, transform, materialId } = properties;
  const { activeWorld } = useAppContext();
  
  // Create points for the lathe
  const points = [];
  for (let i = 0; i < 10; i++) {
    points.push(new THREE.Vector2(Math.sin(i * 0.2) * 0.5 + 0.5, (i - 5) * 0.2));
  }
  
  // In preview mode, use simplified transform
  const position: [number, number, number] = isPreview 
    ? [0, 0, 0] 
    : [transform.position.x, transform.position.y, transform.position.z];
  const rotation: [number, number, number] = isPreview
    ? [0, 0, 0]
    : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale: [number, number, number] = isPreview
    ? [1, 1, 1]
    : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      return material.render(material.properties, isPreview);
    }
    return new THREE.MeshStandardMaterial({ color: "#808080" });
  }, [material, material?.properties, isPreview]);

  return (
    <Lathe
      ref={ref}
      args={[points, segments, phiStart, phiLength]}
      position={position}
      rotation={rotation}
      scale={scale}
      material={meshMaterial}
    />
  );
});

export const LatheMeshItem: LatheMesh = {
  id: '',
  name: 'Lathe',
  type: 'mesh',
  subtype: 'lathe',
  icon: 'Lathe',
  properties: {
    segments: 12,
    phiStart: 0,
    phiLength: Math.PI * 2,
    materialId: '',
    transform: {
      position: new Vector3(),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1)
    }
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.segments',
      type: 'number',
      label: 'Segments',
      default: 12,
      panel: 'geometry'
    },
    {
      key: 'properties.phiStart',
      type: 'number',
      label: 'Phi Start',
      default: 0,
      panel: 'geometry'
    },
    {
      key: 'properties.phiLength',
      type: 'number',
      label: 'Phi Length',
      default: Math.PI * 2,
      panel: 'geometry'
    }
  ],
  render: (properties: LatheMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <LatheComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Mesh>} />;
  }
}; 